import { useEffect } from 'react';
import { LICENSE_KEY_INPUT_ID } from './context';
import { PRO_PLAN, proCtaLabel } from '../../config/pricing';
import { track } from '../analytics/client';
import type { Surface } from '../analytics/catalog';
import './license.css';

/** Small lock glyph marking a Pro element as visible-but-locked (spec §7.5). */
export function ProLock() {
  return (
    <span className="cf-lock" role="img" aria-label="Pro feature, locked">
      {'\u{1F512}'}
    </span>
  );
}

/** Move focus to the single activation form in the rail License panel. */
function focusLicenseKeyInput() {
  const input = document.getElementById(LICENSE_KEY_INPUT_ID);
  // jsdom has no scrollIntoView; in the browser bring the panel into view.
  input?.scrollIntoView?.({ block: 'nearest' });
  input?.focus();
}

/**
 * Inline upgrade prompt shown when a free user engages a locked Pro element
 * (spec §7.5: never hidden — visible, locked, with an upgrade path). Activation
 * itself lives only in the persistent rail LicensePanel; the affordances here
 * move focus there so two identical key forms are never on screen at once.
 *
 * `compact` renders a *quiet* one-liner — a lock, the feature name, and a single
 * link-styled "Unlock with Pro" affordance that routes attention to the one rail
 * License panel (which carries the key form and the trial CTA). It deliberately
 * carries NO pricing paragraph, NO feature list, and NO second trial link, so a
 * free Studio screen that stacks several locked features (Transcribe, video
 * gate, Timing panel, QC-report export) reads as a row of restrained badges
 * rather than a bazaar of full pricing cards (cue-forge-crm; drt.13). The full
 * pitch (`compact={false}`) stays reserved for a single on-demand surface a user
 * has actively engaged — Convert/Batch, a picked Pro export target, the custom
 * preset editor. Same region name across both, so AT and the gating tests see
 * one shape.
 */
export function UpgradePrompt({
  feature,
  surface,
  compact = false,
}: {
  feature: string;
  /** Which gated surface this prompt guards — the analytics label, a closed set. */
  surface: Surface;
  compact?: boolean;
}) {
  // A gate becoming visible IS the "view" — fire once per mount, per surface.
  // This is a genuine visibility event, not a state re-render, so an effect is
  // the right home (analytics user-intent rule allows mount-driven views).
  useEffect(() => {
    track('pro_gate_view', { surface });
  }, [surface]);
  if (compact) {
    return (
      <div className="cf-upgrade cf-upgrade--compact" role="region" aria-label="Upgrade to Pro">
        <span className="cf-upgrade__line">
          <ProLock /> {feature} is a Pro feature.
        </span>
        <button
          type="button"
          className="cf-upgrade__pro-link"
          onClick={() => {
            track('upsell_click', { surface });
            focusLicenseKeyInput();
          }}
        >
          Unlock with Pro
        </button>
      </div>
    );
  }
  return (
    <div className="cf-upgrade" role="region" aria-label="Upgrade to Pro">
      <h2 className="cf-upgrade__title">
        <ProLock /> {feature} is a Pro feature
      </h2>
      <p className="cf-upgrade__hint">
        {PRO_PLAN.name} unlocks the QC compliance report, broadcast formats (TTML, SCC, STL, iTT,
        MCC, EBU-TT-D), the video/waveform Studio, on-device transcription, custom QC presets, Pro
        sync, and batch processing.{' '}
        {PRO_PLAN.trialDays > 0
          ? `Free for ${PRO_PLAN.trialDays} days, then ${PRO_PLAN.priceLabel}`
          : PRO_PLAN.priceLabel}
        {PRO_PLAN.hasMonthly ? `, or ${PRO_PLAN.monthlyPriceLabel}` : ''}.
      </p>
      <ul className="cf-upgrade__features">
        {PRO_PLAN.features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <div className="cf-upgrade__actions">
        {PRO_PLAN.checkoutUrl !== '' && (
          <a
            className="cf-btn cf-btn--primary"
            href={PRO_PLAN.checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('upsell_click', { surface })}
          >
            {proCtaLabel(PRO_PLAN)}
          </a>
        )}
        {PRO_PLAN.hasMonthly && (
          <a
            className="cf-btn"
            href={PRO_PLAN.monthlyCheckoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('upsell_click', { surface })}
          >
            Or {PRO_PLAN.monthlyPriceLabel}
          </a>
        )}
      </div>
      <p className="cf-upgrade__manage">
        Already have a license?{' '}
        <button
          type="button"
          className="cf-upgrade__pro-link"
          onClick={() => {
            track('upsell_click', { surface });
            focusLicenseKeyInput();
          }}
        >
          Activate it
        </button>
      </p>
    </div>
  );
}
